import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context';
import { api } from '@/services/api';
import { Button, Input } from '@/components/common';
import styles from './PlatformForcePasswordChangePage.module.css';

export function PlatformForcePasswordChangePage() {
  const navigate = useNavigate();
  const { clearMustChangePassword, logout, isAuthenticated, mustChangePassword, isPlatformAdmin } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if user doesn't need to change password or isn't authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/platform/login', { replace: true });
      return;
    }
    if (!isPlatformAdmin) {
      navigate('/force-password-change', { replace: true });
      return;
    }
    if (!mustChangePassword) {
      navigate('/platform', { replace: true });
    }
  }, [isAuthenticated, mustChangePassword, isPlatformAdmin, navigate]);

  // Don't render if user shouldn't be on this page
  if (!isAuthenticated || !isPlatformAdmin || !mustChangePassword) {
    return null;
  }

  const validateForm = (): string | null => {
    if (!currentPassword) {
      return 'Current password is required';
    }
    if (!newPassword) {
      return 'New password is required';
    }
    if (newPassword.length < 8) {
      return 'New password must be at least 8 characters';
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match';
    }
    if (currentPassword === newPassword) {
      return 'New password must be different from current password';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.put('/api/platform/auth/password', {
        currentPassword,
        newPassword,
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to change password');
      }

      // Clear the mustChangePassword flag
      clearMustChangePassword();

      // Redirect to platform dashboard
      navigate('/platform', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/platform/login', { replace: true });
  };

  const getPasswordStrength = (password: string): { label: string; color: string; width: string } => {
    if (!password) return { label: '', color: '', width: '0%' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { label: 'Weak', color: 'var(--error)', width: '33%' };
    if (strength <= 3) return { label: 'Fair', color: 'var(--warning)', width: '66%' };
    return { label: 'Strong', color: 'var(--success)', width: '100%' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 className={styles.title}>Password Change Required</h1>
          <p className={styles.subtitle}>Please set a new password to continue</p>
        </div>

        <div className={styles.notice}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>For security, you must change your password before accessing the platform.</span>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && <div className={styles.error}>{error}</div>}

          <Input
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter your current password"
            required
            autoComplete="current-password"
          />

          <div className={styles.passwordField}>
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
              autoComplete="new-password"
            />
            {newPassword && (
              <div className={styles.strengthWrapper}>
                <div className={styles.strengthBar}>
                  <div
                    className={styles.strengthFill}
                    style={{ width: passwordStrength.width, backgroundColor: passwordStrength.color }}
                  />
                </div>
                <span className={styles.strengthLabel} style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
          </div>

          <div className={styles.passwordField}>
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              required
              autoComplete="new-password"
            />
            {confirmPassword && newPassword === confirmPassword && (
              <div className={styles.matchIndicator}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Passwords match
              </div>
            )}
          </div>

          <Button
            type="submit"
            className={styles.submitButton}
            isLoading={isLoading}
          >
            Update Password & Continue
          </Button>
        </form>

        <div className={styles.footer}>
          <button type="button" onClick={handleLogout} className={styles.logoutButton}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
